<template>
  <div :class="['store-locator', { loading: isLoading }]">
    <cp-map
      :options="computedMapOptions"
      :stores="displayedStores"
      :type="type"
    ></cp-map>
    <cp-modal :content="activeStoreContent" ref="modal"></cp-modal>
    <cp-search :options="computedSearchOptions"></cp-search>
    <cp-list :options="computedListOptions" :stores="displayedStores"></cp-list>
  </div>
</template>

<script>
import { array_get } from '../helpers/array_access';
import slugify from '../helpers/slugify';
import List from './list';
import Map from './map';
import Search from './search';
import Modal from './modal';
import template from '../helpers/template';

export default {
  components: {
    'cp-list': List,
    'cp-map': Map,
    'cp-search': Search,
    'cp-modal': Modal,
  },
  name: 'cp-store-locator',
  props: {
    center: {
      default() {
        return array_get(this.config, 'map.options.center');
      },
      type: Object,
    },
    options: {
      type: Object,
    },
    placeholder: {
      default() {
        return array_get(this.config, 'search.placeholder');
      },
      type: String,
    },
    search: {
      default: 'autocomplete', // 'search' or false
      type: [Boolean, String],
    },
    stores: {
      default() {
        return array_get(this.config, 'stores');
      },
      type: [String, Array],
    },
    selectOptions: {
      default() {
        return array_get(this.config, 'search.select');
      },
      type: [String, Array],
    },
    type: {
      default() {
        return array_get(this.config, 'type');
      },
      type: String,
    },
    zoom: {
      default() {
        return array_get(this.config, 'map.options.zoom');
      },
      type: [Number, String],
    },
  },
  data() {
    return {
      isLoading: true,
      storesList: [],
      displayedStores: [],
      activeStoreContent: null,
    };
  },
  computed: {
    computedListOptions() {
      return {};
    },

    computedMapOptions() {
      return {
        ...this.getConfig('map'),
        infoWindowTemplate: this.getConfig('templates.infoWindow'),
        options: {
          ...this.getConfig('map.options'),
          center: this.center,
          zoom: this.zoom,
        },
      };
    },

    computedSearchOptions() {
      const options = {
        ...this.getConfig('search'),
        placeholder: this.placeholder,
        selectOptions: this.selectOptions,
      };

      if (this.search === 'search') {
        return {
          ...options,
          autocomplete: false,
        };
      } else if (this.search === 'autocomplete') {
        return options;
      }

      return false;
    },
  },
  methods: {
    /**
     * Display only stores where given keys match the given query.
     * @param query
     * @param keys
     * @param strict
     * @return {void}
     */
    filterStores(query, keys = null, strict = false) {
      if (!query) {
        this.displayedStores = [].concat(this.storesList);

        return;
      }

      if (!keys) {
        keys = Object.keys(this.storesList[0]);
      }

      this.displayedStores = this.storesList.filter((store) => {
        const found = keys.map((key) => {
          if (strict) {
            return store[key] === query;
          }

          return new RegExp(`(${query.split(' ').join('|')})`, 'gi').test(
            store[key]
          );
        });

        return found.filter(Boolean).length > 0;
      });
    },

    /**
     * Format given stores.
     */
    formatStores() {
      this.storesList = [].concat(this.stores).map((store) => {
        store.uid = this.$action.uid(store);
        return store;
      });

      if (typeof this.getConfig('formatStore') === 'function') {
        this.storesList = this.storesList.map((store) =>
          this.getConfig('formatStore')(store)
        );
      }

      this.displayedStores = [].concat(this.storesList);
    },

    /**
     * Show only given selected markers or all markers if selected == null.
     * @param {Array|null} selected - Array of stores uid to display
     * @return {[]}
     */
    setDisplayedMarkers(selected = null) {
      if (selected) {
        return (this.displayedStores = this.storesList.filter(
          (store) => selected.indexOf(store.uid) > -1
        ));
      }

      return (this.displayedStores = [].concat(this.storesList));
    },
  },
  beforeMount() {
    // internal global helpers
    this.$action.register('loading', (status) => {
      this.isLoading = status;
    });

    this.$action.register('uid', ({ lat, lng, title }) => {
      return slugify(`marker-${lat}-${lng}-${title}`);
    });

    if (typeof this.stores === 'string') {
      this.displayedStores = [];

      // get stores from 'this.stores' url
      fetch(this.stores)
        .then((res) => res.json())
        .then((stores) => {
          this.stores = stores;
          this.formatStores();
        });
    } else {
      this.formatStores();
    }

    // switch (this.type) {
    //     case 'google-map' :
    //         this.loadGoogleMapsApi(this.getConfig('api'));
    //         break;
    //
    //     case 'open-street-map' :
    //         this.loadOpenStreetMapApi(this.getConfig('api'));
    //         break;
    //
    //     default : break;
    // }
  },
  mounted() {
    this.trace('{store-locator.vue} mounted');

    this.$bus.on('filter', (query, keys, strict) =>
      this.filterStores(query, keys, strict)
    );
    this.$bus.on('set-displayed-markers', (selected) =>
      this.setDisplayedMarkers(selected)
    );
    this.$bus.on('active-marker', (uid) => {
      const store = this.stores.find((obj) => {
        return obj.uid === uid;
      });

      let content = template(this.computedMapOptions.infoWindowTemplate, store);

      if (typeof this.getConfig('formatInfoWindow') === 'function') {
        content = this.getConfig('formatInfoWindow')(
          store,
          this.computedMapOptions.infoWindowTemplate
        );
      }

      this.activeStoreContent = content;

      this.$refs.modal.open();
    });
  },
};
</script>
